import { useState, useEffect } from 'react';
import {
  ButtonSize,
  useTranslation,
  useSession,
  useNextgenRouter,
  AccountType,
  useFeatureVisibility,
} from '@nextgen-web-framework/all';
import {
  subscriptionApi,
  SubscriptionRequest,
  SubscriptionResponse,
  UpdateSubscriptionRequest,
  SubscriptionType,
  updateSubscriptionDetails,
  updateShowGcoConfirmationModal,
  ElementTemplateData,
  Coupons,
  updateTncAccepted,
  getSubscriptionDetailsSliceReq,
  updateSelectedSubscription,
  nullishOperator,
  type BundleSlot,
  type ErrorResponse,
  type SlotEntry,
  type SubscriptionElement,
  type SubscriptionProductEntry,
  PlanResponseTypeEnum,
  updateAutoContinueSop,
} from '@commerce-subscription-webcomponents/sdk';
import { useDispatch, useSelector } from 'react-redux';
import { type AppState, type AppDispatch, Logger, StorageService } from '@nextgen-web-framework/core';
import { authorizePayment } from '@commerce-payment-webcomponents/sdk';
import { AmwayUserDetails, PromotionalItemOptions } from '@commerce-cart-webcomponents/sdk';
import { BundleType, Slot, productDataTransformer } from '@commerce-product-webcomponents/sdk';
import { getBenefitRowKey } from '@commerce-promotion-webcomponents/ui';
import { checkoutFulfillmentOptions } from '@commerce-checkout-webcomponents/sdk';
import useHandleException from '../../../hooks/useHandleException';
import { amwayUserData, getCurrentElementFromSubscription } from '../../../utils/utils';
import LoginRedirectModel from '../LoginRedirectModel/LoginRedirectModel';
import { SubscribeDetailsDrawer, useSubscriptionQuantityContext } from '../../subscription-plan/molecules';
import GcoConfirmationModal from '../GcoConfirmationModal/GcoConfirmationModal';
import { ProductListType } from '../../subscription-plan/basketsize-template/types/propTypes';
import { SopLocalStorageKeys } from '..';
import { DataFlowEnum, type SubscribeEventPaylod, type SubscribeButtonPropTypes } from './types/propTypes';
import SubscribeCtaWithIcon from './SubscribeCtaWithIcon/SubscribeCtaWithIcon';

export type PromotionalItemOptionsNew = PromotionalItemOptions & { id: string };

// Key for manage planId and productId in the session
const selectedPlanAndProductKey = 'sop_ppid';

const SubscribeButton = ({
  productId,
  quantity = 1,
  productType,
  disabled = false,
  size = ButtonSize.SMALL,
  dataFlow,
  onSuccess,
  bundleSlots,
  productList,
  isBasketSizePlan = false,
  detailsForCartApiCall,
}: SubscribeButtonPropTypes) => {
  const loggerInstance = Logger.getInstance({
    component: 'SubscribeButton',
    product: 'Subscription',
  });
  const {
    selectedSubscriptionPlan,
    planOrderBenefitSelections,
    subscriptionDetails,
    bundleState,
    profile,
    showGcoConfirmationModal,
    autoContinueSop,
  } = useSelector((state: AppState) => state.commerceSubscription);
  const dispatch = useDispatch<AppDispatch>();
  const t = useTranslation();
  const { isAnonymous, session } = useSession();
  const amwayUser = amwayUserData(session);
  const { enableGroupOrder, isCartPresent, addToCart, createCart } = detailsForCartApiCall || {};
  const { push, pathname } = useNextgenRouter();
  const sessionStorage = StorageService.getSessionStorage();
  const localStorage = StorageService.getLocalStorage();
  const planContext = useSubscriptionQuantityContext();

  const [isVisibleModal, setIsVisibleModal] = useState(false);
  const [displaySubscriptionTypeDrawer, setDisplaySubscriptionTypeDrawer] = useState(false);
  const [subscriptionAllowedType, setSubscriptionAllowedType] = useState<SubscriptionType[]>([
    SubscriptionType.Automatic,
    SubscriptionType.Manual,
  ]);
  const [isLoading, setIsLoading] = useState(false);
  const { handleException } = useHandleException({
    toast: { enabled: true },
    logServiceInstanceMethod: loggerInstance.error,
  });
  const configs = ConfigService.getConfigs();
  const SHOULD_ENABLE_PICKUP = configs.NEXT_SOP_GMN_PLAN_FEATURE;
  const sopEnhancedUxEnabled = configs.NEXT_SOP_ENHANCED_UX_ENABLED as boolean;
  const displayAutoContinue = useFeatureVisibility('subscription.enableAutoContinue');

  const setShowModel = (show: boolean) => {
    dispatch(updateShowGcoConfirmationModal(show));
  };

  useEffect(() => {
    setSubscriptionAllowedType(selectedSubscriptionPlan?.plan?.subscriptionTypes || [SubscriptionType.Automatic]);
  }, [selectedSubscriptionPlan]);

  const getManualSubscriptionPayload = () => ({
    planId: subscriptionDetails.plan?.id,
    version: subscriptionDetails?.version,
  });

  const getAutomaticSubscriptionPayload = () => ({
    planId: subscriptionDetails.plan?.id,
    profile: {
      id: dataFlow === DataFlowEnum.RETRY ? subscriptionDetails?.profile?.id : null,
      name: dataFlow === DataFlowEnum.RETRY ? subscriptionDetails?.profile?.name : profile.name,
      payment: profile.payment,
      type: SubscriptionType.Automatic,
      amwayUser: amwayUserData(session),
      ...(profile?.fulfillment
        ? {
            fulfillment: profile?.fulfillment,
          }
        : {}),
      deliveryAddressId:
        profile?.fulfillment?.id === checkoutFulfillmentOptions.BOPIS
          ? null
          : dataFlow === DataFlowEnum.RETRY
            ? subscriptionDetails?.profile?.deliveryAddress?.id
            : profile.deliveryAddress.contactId,
      deliveryOptionId:
        profile?.fulfillment?.id === checkoutFulfillmentOptions.BOPIS
          ? null
          : dataFlow === DataFlowEnum.RETRY
            ? subscriptionDetails?.profile?.deliveryOption?.id
            : profile?.deliveryOption?.id || 'standard',
    },
    // processDay: dataFlow === DataFlowEnum.RETRY ? subscriptionDetails?.profile?.processDay : profile.processDay,
    processDay: subscriptionDetails.processDay,
    deliveryOption: subscriptionDetails.deliveryOption,
    ...(SHOULD_ENABLE_PICKUP ? { executeToday: subscriptionDetails.executeToday } : {}),
    version: subscriptionDetails?.version,
  });

  const getBundleSlots = (product: ProductListType) => {
    // In the Basket size plan we are only considering Fixed Bundple Product and NOT Dynamic Bunlde Product
    if (!(product.productType === 'BUNDLE')) {
      return undefined;
    }

    const bundleSlots: BundleSlot[] =
      product?.bundle?.slots?.map(
        (slot: Slot) =>
          ({
            id: `${slot.id}`,
            entries: slot.products?.map((_product) => ({
              product: {
                id:
                  product?.bundle?.type === BundleType.Fixed
                    ? productDataTransformer(_product)?.productId
                    : _product?.id,
              },
              quantity: slot.minQty as number,
            })),
          }) as SlotEntry,
      ) || ([] as BundleSlot[]);
    return bundleSlots;
  };

  const getPayloadEntries = () => {
    // Payload logic for Subscription with Product selected under basket size plan
    if (isBasketSizePlan) {
      const entries: SubscriptionProductEntry[] = [];
      (productList || []).forEach((product) => {
        if (product.quantity > 0) {
          const entry: SubscriptionProductEntry = {
            product: {
              id: product.productId,
            },
            quantity: product.quantity,
            productType: product.productType,
            // If user has selected fixed bundle in the basket size plan then we have to pass it's slots details
            bundleSlots: getBundleSlots(product),
          };
          entries.push(entry);
        }
      });
      return entries;
    }

    const bundleSlotEntries = dataFlow === DataFlowEnum.PDP ? bundleSlots : bundleState;

    // Payload logic for Subscription with Standard Product / Fixed Bundle Plan / Dynamic Plan
    return [
      {
        product: {
          id: productId,
        },
        quantity: quantity,
        productType: productType,
        bundleSlots: productType === 'BUNDLE' ? bundleSlotEntries : undefined,
      },
    ];
  };

  if (dataFlow === DataFlowEnum.PDP || dataFlow === DataFlowEnum.PLAN_DETALS) {
    const onSubscribeClick = () => {
      if (!isAnonymous()) {
        setIsVisibleModal(false);
        // If Manual Subscription is allowed on this plan then we are displaying Subscription Type Drawer
        if (selectedSubscriptionPlan?.plan?.subscriptionTypes?.indexOf(SubscriptionType.Manual) !== -1) {
          setDisplaySubscriptionTypeDrawer(true);
        } else {
          createSubscription(SubscriptionType.Automatic);
        }
      } else {
        setIsVisibleModal(true);
      }
    };

    const createSubscription = (selectedType: SubscriptionType, selectedProfile?: AmwayUserDetails) => {
      const isManualSop = selectedType === SubscriptionType.Manual;
      const isPDPAndSopEnabledAndManual =
        (dataFlow === DataFlowEnum.PDP || dataFlow === DataFlowEnum.PLAN_DETALS) && sopEnhancedUxEnabled && isManualSop;

      let payload: SubscriptionRequest = {
        type: selectedType,
        amwayUser: amwayUserData(session),
        planId: selectedSubscriptionPlan?.planId,
        entries: getPayloadEntries(),
        userChoiceBenefit: getUserChoiceBenefits(),
        ...(displayAutoContinue && selectedSubscriptionPlan?.plan?.allowAutoContinue
          ? { autoContinue: autoContinueSop }
          : {}),
      };

      if (selectedSubscriptionPlan?.plan?.type === PlanResponseTypeEnum.GMN) {
        const itemFixedPrices =
          selectedSubscriptionPlan?.plan?.elementTemplates?.[0]?.coupons?.[0]?.itemFixedPrices?.[0];
        if (itemFixedPrices?.price) {
          payload.subscribeAmount = itemFixedPrices.price;
        }
      }

      if (selectedProfile?.amwayUserId) {
        payload = {
          ...payload,
          isDownline: true,
          parentAccountId: amwayUser?.accountId, // accountId of the logged in user
          parentAmwayUser: amwayUser, // logged in user Info
          amwayUser: {
            // user details of the selected downline user
            userId: selectedProfile.amwayUserId,
            firstName: selectedProfile.firstName,
            lastName: selectedProfile.lastName,
            accountId: nullishOperator(selectedProfile.accountId, '') as string,
            accountType: selectedProfile.accountType as AccountType,
          },
        };
      }

      if (isPDPAndSopEnabledAndManual) {
        dispatch(updateSubscriptionDetails({}));
      }
      setIsLoading(() => true);
      subscriptionApi
        .createSubscription({
          payload: payload,
        })
        .then((res: SubscriptionResponse) => {
          if (res) {
            if (isPDPAndSopEnabledAndManual) {
              const currentElement = getCurrentElementFromSubscription(res) as SubscriptionElement;
              const { allowChangeProductVariant } = currentElement;
              if (!allowChangeProductVariant) {
                dispatch(
                  updateSubscriptionDetails({
                    ...res,
                  }),
                );
              } else {
                dispatch<AppState>(
                  getSubscriptionDetailsSliceReq({
                    subscriptionId: res?.id,
                  }),
                );
              }
            }

            StorageService.getLocalStorage().store('SubscriptionReqVersion', res.version);
            setIsLoading(() => false);
            // Clear AutoContinueSop redux state
            dispatch(updateAutoContinueSop(false));

            const isSuccessCallbackConditionMet = onSuccess && (sopEnhancedUxEnabled ? !isManualSop : true);
            if (isSuccessCallbackConditionMet) {
              const searchParams = new URLSearchParams(window.location.search?.toString());
              const pageUrl = searchParams.toString() ? `${pathname}?${searchParams.toString()}` : pathname;
              localStorage.store(SopLocalStorageKeys.SopBackUrl, nullishOperator(pageUrl, ''));

              onSuccess({
                subscriptionId: res?.id,
                type: res.type,
              });
            }
          }
        })
        .catch((error: ErrorResponse) => {
          setDisplaySubscriptionTypeDrawer(false);
          setIsLoading(() => false);
          handleException(error, {
            identifier: 'Error in creating subscription',
          });
        });
    };

    const getUserChoiceBenefits = () => {
      const userChoiceBenefitPresent = selectedSubscriptionPlan?.plan?.requiresUserSelection;
      if (userChoiceBenefitPresent) {
        const elementsWithBenefits = selectedSubscriptionPlan?.plan?.elementTemplates?.filter(
          (element: ElementTemplateData) => element?.requiresUserSelection,
        );
        const getCouponCode = (coupons: Coupons[]) => nullishOperator(coupons?.[0].code, '') as string;
        const getPromotionId = (element: ElementTemplateData) =>
          element?.coupons?.[0]?.promotionalItemOptions?.[0]?.prmId;
        // const getItemSet = (element: ElementTemplateData) =>
        //   element?.coupons?.[0]?.promotionalItemOptions?.[0]?.promotionalItems?.[0]?.itemSet;

        const transformedElements = elementsWithBenefits?.map((element: ElementTemplateData) => {
          const { coupons, elementSequence, cycleSequence } = element;
          // const itemSet = getItemSet(element) as ProductDetails[];
          const promotionId = getPromotionId(element);
          const promotionalItemOptions = coupons?.[0]?.promotionalItemOptions;

          const entries = getUserChoiceBenefitsEntries(
            promotionalItemOptions as PromotionalItemOptionsNew[],
            elementSequence as number,
          );
          return {
            coupon: getCouponCode(coupons as Coupons[]),
            elementSequence,
            cycleSequence,
            promotionId,
            entries,
          };
        });
        return transformedElements;
      }
      return undefined;
    };

    const getUserChoiceBenefitsEntries = (
      promotionalItemOptions: PromotionalItemOptionsNew[] | undefined,
      elementSequence: number,
    ) => {
      const planOrderBenefitSelectionsForElement = planOrderBenefitSelections.find(
        (orderBenefits: { orderId: number }) => orderBenefits?.orderId === elementSequence,
      );
      const selectedProductFromItems: SubscriptionProductEntry[] = [];
      promotionalItemOptions?.forEach((itemOption) => {
        const rowKey = getBenefitRowKey(itemOption?.id, itemOption?.giftPromotion);
        const innerObject = planOrderBenefitSelectionsForElement?.selectedProducts?.[rowKey || ''] || {};
        const itemSet = itemOption?.promotionalItems?.[0]?.itemSet || [];

        const productFromItems = itemSet.filter((item) => Object.prototype.hasOwnProperty.call(innerObject, item?.id));
        productFromItems.forEach((item) => {
          if (innerObject[item?.id ?? '']?.quantity === 0) {
            return;
          }
          const obj: SubscriptionProductEntry = {
            product: {
              id: item?.id,
            },
            ...(item?.type ? { productType: item?.type } : {}),
            quantity: innerObject[item?.id || '']?.quantity,
          };
          selectedProductFromItems.push(obj);
        });
      });
      // const innerObject =
      //   planOrderBenefitSelectionsForElement &&
      //   planOrderBenefitSelectionsForElement?.selectedProducts &&
      //   planOrderBenefitSelectionsForElement?.selectedProducts[promotionId];
      // const selectedProductFromItems = itemSet.filter((item) =>
      //   Object.prototype.hasOwnProperty.call(innerObject, item?.id),
      // );
      return selectedProductFromItems;
    };

    const closeDrawer = (refreshPage?: boolean) => {
      dispatch(updateTncAccepted(false));
      setDisplaySubscriptionTypeDrawer(false);

      if (sopEnhancedUxEnabled && refreshPage) {
        if (dataFlow == DataFlowEnum.PDP) {
          sessionStorage.remove(selectedPlanAndProductKey);
          dispatch(updateSelectedSubscription({}));
          const searchParams = new URLSearchParams(window.location.search?.toString());
          searchParams.delete('sop');
          const newUrl = searchParams.toString() ? `${pathname}?${searchParams.toString()}` : pathname;
          push(newUrl as string, { scroll: false });
        } else if (dataFlow == DataFlowEnum.PLAN_DETALS && planContext?.reloadPlanDetails) {
          planContext?.reloadPlanDetails();
        }
      }
    };

    return (
      <>
        <SubscribeCtaWithIcon
          key={`${disabled}-${isLoading}`}
          id={'subscribe-button'}
          data-testid="subscribe-button-testid"
          onClick={onSubscribeClick}
          title={t('subscription.subscriptionSelection.subscribeButtonText')}
          size={size}
          disabled={disabled || isLoading}
          isLoading={isLoading}
        />

        {isVisibleModal && <LoginRedirectModel setIsVisibleModal={setIsVisibleModal} />}

        {displaySubscriptionTypeDrawer && (
          <SubscribeDetailsDrawer
            closeDrawer={closeDrawer}
            createSubscription={createSubscription}
            subscriptionAllowedType={subscriptionAllowedType}
            allowDownlineSubscription={selectedSubscriptionPlan?.plan?.allowDownlineSubscription}
            isLoading={isLoading}
          />
        )}
      </>
    );
  } else {
    const updateSubscription = (updateCart: boolean) => {
      const payload: UpdateSubscriptionRequest = getPayload();
      setIsLoading(() => true);
      subscriptionApi
        .updateSubscription({
          subscriptionId: subscriptionDetails.id,
          payload: payload,
        })
        .then((response: SubscriptionResponse) => {
          handleResponse(response, updateCart);
        })
        .catch((error: ErrorResponse) => {
          setIsLoading(() => false);
          handleException(error, {
            identifier: 'Error in updating subscription',
          });
        });
    };

    const getPayload = () =>
      subscriptionDetails.type === SubscriptionType.Manual
        ? getManualSubscriptionPayload()
        : getAutomaticSubscriptionPayload();

    const handleResponse = (response: AppState, updateCart: boolean) => {
      dispatch(
        updateSubscriptionDetails({
          ...subscriptionDetails,
          ...response,
        }),
      );

      if (subscriptionDetails.type === SubscriptionType.Manual && updateCart) {
        if (response) {
          updateUserCart({ ...subscriptionDetails, ...response });
        } else {
          updateUserCart(subscriptionDetails);
        }
      } else if (response?.profile?.payment) {
        setIsLoading(() => false);
        authorizePayment(response.profile.payment.paymentDetails);
      } else {
        handleRedirect();
      }
    };

    const handleRedirect = () => {
      if (onSuccess) {
        const isErrorInUpdateCart = enableGroupOrder ? true : false;
        const redirectUrl = isErrorInUpdateCart
          ? `/subscription-confirmation/${subscriptionDetails.id}?isErrorInUpdateCart=true`
          : `/subscription-confirmation/${subscriptionDetails?.id}`;
        push(redirectUrl);
      }
    };

    const onButtonClick = () => {
      if (enableGroupOrder) {
        dispatch(updateShowGcoConfirmationModal(true));
      } else {
        updateSubscription(subscriptionDetails.type === SubscriptionType.Manual ? true : false);
      }
    };

    const updateUserCart = (subscriptionDetails: SubscriptionResponse) => {
      if (isCartPresent && addToCart) {
        addToCart(subscriptionDetails);
      } else if (createCart) {
        createCart(subscriptionDetails);
      }
    };

    const onContinueClick = () => {
      setShowModel(false);
      updateSubscription(true);
    };

    const onCancelClick = () => {
      setShowModel(false);
      updateSubscription(false);
    };

    return (
      <>
        {showGcoConfirmationModal && (
          <GcoConfirmationModal onCancelClick={onCancelClick} onContinueClick={onContinueClick} />
        )}
        <SubscribeCtaWithIcon
          key={`${disabled}-${isLoading}`}
          id={'subscribe-button'}
          data-testid="subscribe-button-testid"
          onClick={onButtonClick}
          title={t('subscription.subscriptionSelection.subscribeButtonText')}
          size={size}
          disabled={disabled || isLoading}
          isLoading={isLoading}
        />
      </>
    );
  }
};

export { type SubscribeEventPaylod, type SubscribeButtonPropTypes, DataFlowEnum };
export default SubscribeButton;
